import { IndexedDBService } from '../storage/IndexedDBService';
import { ApiService } from '../api/ApiService';

interface SyncItem {
  id: string;
  type: 'CREATE' | 'UPDATE' | 'DELETE';
  entity: string;
  data: any;
  timestamp: string;
  retries: number;
  status: 'pending' | 'processing' | 'failed';
  error?: string;
}

/**
 * Mapping des noms d'entités (stockage local) vers les endpoints API
 * Les noms de stockage utilisent des underscores, mais l'API utilise des tirets
 */
const ENTITY_TO_ENDPOINT: Record<string, string> = {
  'journal_entries': 'journal-entries',
  'agent_entries': 'agent-entries',
  'fiscal_years': 'fiscal-years',
  'business_units': 'business-units',
  'treasury_accounts': 'treasury/accounts',
  'cash_flows': 'treasury/cash-flows',
  // Entités qui n'ont pas besoin de mapping (même nom)
  'accounts': 'accounts',
  'ledger': 'ledger',
  'declarations': 'declarations',
  'notifications': 'notifications',
  'users': 'users',
  'settings': 'settings',
};

export class SyncService {
  private static SYNC_INTERVAL = 30000; // 30 secondes
  private static MAX_RETRIES = 3;
  private static SYNC_QUEUE_STORE = 'sync_queue';
  private static STATUS_STORE = 'sync_status';
  private static syncInterval: NodeJS.Timeout | null = null;

  static async initialize(): Promise<void> {
    try {
      await IndexedDBService.initDB();
      
      // Démarrer la synchronisation périodique
      if (navigator.onLine) {
        this.startSync();
      }

      // Configurer les écouteurs d'événements
      window.addEventListener('online', () => this.startSync());
      window.addEventListener('offline', () => this.stopSync());
    } catch (error) {
      console.error('Failed to initialize SyncService:', error);
    }
  }

  static async addToSyncQueue(item: Omit<SyncItem, 'timestamp' | 'retries' | 'status'>): Promise<void> {
    const syncItem: SyncItem = {
      ...item,
      timestamp: new Date().toISOString(),
      retries: 0,
      status: 'pending'
    };

    await IndexedDBService.add(this.SYNC_QUEUE_STORE, syncItem);
    
    if (navigator.onLine) {
      await this.processSyncQueue();
    }
  }

  private static async processSyncQueue(): Promise<void> {
    if (!navigator.onLine) return;

    // Réessayer les pending ET les failed (qui n'ont pas encore atteint
    // MAX_RETRIES). Sans ça un échec transitoire (token expiré 5min)
    // bloque l'item à 1/3 sans jamais retenter.
    const [pendingItems, failedItems] = await Promise.all([
      IndexedDBService.getByIndex<SyncItem>(this.SYNC_QUEUE_STORE, 'status', 'pending'),
      IndexedDBService.getByIndex<SyncItem>(this.SYNC_QUEUE_STORE, 'status', 'failed'),
    ]);
    const items = [...pendingItems, ...failedItems.filter(i => (i.retries || 0) < this.MAX_RETRIES)];

    for (const item of items) {
      try {
        // Marquer comme en cours de traitement
        await IndexedDBService.update(this.SYNC_QUEUE_STORE, {
          ...item,
          status: 'processing'
        });

        await this.processSyncItem(item);

        // Supprimer l'item après synchronisation réussie
        await IndexedDBService.delete(this.SYNC_QUEUE_STORE, item.id);
      } catch (error) {
        await this.handleSyncError(item, error);
      }
    }
  }

  private static async processSyncItem(item: SyncItem): Promise<void> {
    // Mapper le nom d'entité vers l'endpoint API correct
    const entityName = item.entity.toLowerCase();
    const apiEndpoint = ENTITY_TO_ENDPOINT[entityName] || entityName.replace(/_/g, '-');
    const endpoint = `/${apiEndpoint}`;
    let response: any;

    switch (item.type) {
      case 'CREATE':
        response = await ApiService.post(endpoint, item.data);
        break;
      case 'UPDATE':
        response = await ApiService.put(`${endpoint}/${item.data.id}`, item.data);
        break;
      case 'DELETE':
        response = await ApiService.delete(`${endpoint}/${item.data.id}`);
        break;
    }

    if (!response?.success) {
      // Inclure le message d'erreur réel du backend pour le diagnostic
      const serverMessage =
        response?.error?.message ??
        response?.message ??
        (Array.isArray(response?.error) ? response.error.join(' ; ') : null) ??
        'API request failed';
      throw new Error(`[${item.type} ${item.entity}] ${serverMessage}`);
    }
  }

  private static async handleSyncError(item: SyncItem, error: any): Promise<void> {
    const errorMessage = error instanceof Error ? error.message : String(error ?? 'Unknown error');
    // Log explicite : sans ça, l'utilisateur ne sait jamais pourquoi son
    // écriture n'a pas atterri dans le backend (entrée locale persistée
    // dans IndexedDB mais jamais en base ⇒ disparaît au prochain reset).
    console.error(`[SyncService] ${item.type} ${item.entity} échec :`, errorMessage, item);

    const updatedItem: SyncItem = {
      ...item,
      retries: item.retries + 1,
      status: 'failed',
      error: errorMessage,
    };

    if (updatedItem.retries >= this.MAX_RETRIES) {
      // Archiver dans sync_failures POUR exposer à l'UI (banner, settings,
      // etc.). On supprime de sync_queue pour ne pas spammer indéfiniment
      // mais on conserve une trace consultable.
      await IndexedDBService.add('sync_failures', updatedItem);
      await IndexedDBService.delete(this.SYNC_QUEUE_STORE, item.id);
      // Émettre un événement custom pour permettre à l'UI d'afficher
      // une notification immédiate.
      try {
        window.dispatchEvent(new CustomEvent('wanzo:sync:failure', {
          detail: { item: updatedItem, error: errorMessage },
        }));
      } catch { /* environnement sans window — silencieux */ }
    } else {
      await IndexedDBService.update(this.SYNC_QUEUE_STORE, updatedItem);
    }
  }

  private static startSync(): void {
    if (this.syncInterval) return;
    
    this.processSyncQueue(); // Synchronisation immédiate
    this.syncInterval = setInterval(() => {
      this.processSyncQueue();
    }, this.SYNC_INTERVAL);
  }

  private static stopSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  static async getSyncStatus(): Promise<{
    pendingCount: number;
    failedCount: number;
    lastSync?: string;
  }> {
    const [pending, failed] = await Promise.all([
      IndexedDBService.getByIndex(this.SYNC_QUEUE_STORE, 'status', 'pending'),
      IndexedDBService.getByIndex(this.SYNC_QUEUE_STORE, 'status', 'failed')
    ]);

    const lastSync = await IndexedDBService.get(this.STATUS_STORE, 'lastSync');

    let lastSyncTimestamp: string | undefined = undefined;
    if (typeof lastSync === 'object' && lastSync !== null && 'timestamp' in lastSync && typeof (lastSync as any).timestamp === 'string') {
      lastSyncTimestamp = (lastSync as any).timestamp;
    }

    return {
      pendingCount: pending.length,
      failedCount: failed.length,
      lastSync: lastSyncTimestamp
    };
  }
}